import { handleSystemErrorReport } from '@mb/lib/utils';
import { ResultItem } from 'components/components/SearchAutocompleteResults/SearchAutocompleteResults.types';
import {
  DefaultSuggestionResponse,
  LocalHistoryItem,
  SourceType,
  SuggestionResponse,
} from '../types';

const SUGGESTIONS_ITEMS_COUNT = 9;
const DEFAULT_SUGGESTIONS_ITEMS_COUNT = 5;
export const WORKSPACE_PATH = '/workspace';
export const BRANDS_PATH = '/brands';
const SEARCH_PATH = '/search';
const INSPIRATIONS_PATH = '/inspiration';
const MIN_LENGTH_QUERY = 3;
const currentPath = window.location.pathname;

export const isSearchPage = currentPath?.includes(SEARCH_PATH);
const isInspirationPage = currentPath?.includes(INSPIRATIONS_PATH);

export const getSearchUrl = (query: string, page = 1) =>
  `/search?q=${query}#/page:${page}`;

export const getEmail = () => {
  try {
    const cache = localStorage.getItem('mage-cache-storage');
    const storage = JSON.parse(cache ?? '{}');
    return storage?.customer?.email ?? null;
  } catch (err) {
    handleSystemErrorReport(`cannot get email: ${err.message}`, 'getEmail');
    return null;
  }
};

export const getSubtitle = (sourceType: SourceType | undefined) => {
  switch (sourceType) {
    case SourceType.Category:
      return 'in Categories';
    case SourceType.Product:
      return 'in Products';
    case SourceType.Brand:
      return 'in Brands';
    case SourceType.Completion:
    case undefined:
    default:
      return undefined;
  }
};

export const getSuggestions = (payload?: SuggestionResponse) => {
  if (!payload?.alternatives) {
    return null;
  }
  return {
    items: payload.alternatives
      .map((item) => ({
        value: item.text,
        id: item.text,
        score: item.score,
        subtitle: getSubtitle(item.sourceType),
        sourceType: item.sourceType,
      }))
      .slice(0, SUGGESTIONS_ITEMS_COUNT),
  };
};

export const getSuggestedItems = (payload?: SuggestionResponse) => {
  if (!payload?.items) {
    return [];
  }

  return payload.items || [];
};

export const getDefaultSuggestions = (
  payload?: DefaultSuggestionResponse[],
) => {
  if (!payload || payload.length === 0) {
    return null;
  }
  const trending = payload.find((item) => item.type === 'trending');
  const popular = payload.find((item) => item.type === 'popular');

  const valuesToSlice =
    trending?.values != null
      ? trending.values
      : popular?.values != null
      ? popular.values
      : [];

  const slicedValues = valuesToSlice.slice(0, DEFAULT_SUGGESTIONS_ITEMS_COUNT);
  const items = slicedValues.map((item) => ({
    value: item.query,
    id: `${item.query}_trending`,
    score: item.count,
    type: 'trending',
  }));
  return {
    items,
  };
};

export const getShouldShowSuggestions = (
  query: string,
  history?: ResultItem[],
): boolean => {
  return (
    query.length >= MIN_LENGTH_QUERY ||
    (history?.length === 0 && query.length < MIN_LENGTH_QUERY)
  );
};

export const getShouldShowHistory = (
  query: string,
  history: LocalHistoryItem[] = [],
): boolean => {
  return query.length < MIN_LENGTH_QUERY && history?.length > 0;
};

export const shouldRedirectToSearch = (): boolean => {
  const email = getEmail();
  if (isSearchPage) {
    return false;
  } else if (isInspirationPage) {
    return email === null;
  } else {
    return true;
  }
};

const configGlobal = typeof SEARCH_CONFIG !== 'undefined' ? SEARCH_CONFIG : {};

export const getSiteId = (): string => {
  const email = getEmail();
  if (isInspirationPage) {
    return email === null
      ? configGlobal?.product?.siteId || 'materialbank_product'
      : configGlobal?.inspiration?.siteId || 'materialbank_inspiration';
  } else {
    return configGlobal?.product?.siteId || 'materialbank_product';
  }
};

export const getContext = (): string => {
  const email = getEmail();
  if (isInspirationPage) {
    return email === null ? 'catalog' : 'inspiration';
  } else {
    return 'catalog';
  }
};

export const getShouldShowImages = (
  query: string,
  isLoading: boolean,
): boolean => {
  const email = getEmail();
  if (email === null) {
    return false;
  }
  if (query.length < MIN_LENGTH_QUERY && isLoading) {
    return true;
  }
  return false;
};

export const getResults = (defaultResults: any[]) => {
  return defaultResults;
};

export const getIsBrands = (): boolean => {
  return currentPath?.includes('/brands');
};

export const getQueryFromSS = () => {
  return sessionStorage.getItem('persistentQuery');
};

export const isValidLink = (value: string) => {
  let url;
  try {
    url = new URL(value);
  } catch (_) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

const mainPages = ['/search', '/inspiration'];

export const isMainTabsBrowserHistory = () => {
  if (!isValidLink(document.referrer)) {
    return false;
  }
  const prevUrl = new URL(document.referrer);
  return (
    mainPages.includes(prevUrl?.pathname) &&
    mainPages.includes(window.location.pathname)
  );
};

export const shouldTriggerSearch = (query: string) => {
  const email = getEmail();
  // Do not trigger for Product Catalog, and for unlogged users
  if (email === null && !window.location.pathname.includes('/search')) {
    return false;
  }

  // Do not trigger for Product Catalog, because there is URL param reading
  if (query.length > 0 && !window.location.pathname.includes('/search')) {
    return true;
  }
  return false;
};

// This function converts a file to base64 format
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not a string'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

// This function converts a file to a format suitable for upload
export const convertFileToUploadFormat = async (file: File) => {
  const base64file = await fileToBase64(file);
  return {
    file_name: file.name,
    base64: base64file,
    size: file.size,
    type: file.type,
  };
};
